import {
	UIEventHandler,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import * as React from "react";
import classNames from "classnames";
import { uniqueId } from "lodash-es";
import { Icon } from "./Icon";
import { ScrollToElementContext } from "@mksap/components/context/ScrollToElementContext";
import { inModalRootPortal } from "../utils/inModalRootPortal";
import { withErrorBoundary } from "../errors/withErrorBoundary";
import DialogError from "../errors/DialogError";
import { closest } from "@mksap/utils/jQueryShim";
import { createFocusTrap } from "focus-trap";
import {
	scrollToElement,
	ScrollToElementOptions,
} from "@mksap/utils/scrollToElement";
import { CloseButton } from "./MksapReactBootstrap";

import "./Dialog.scss";

let openModalCount = 0;

export interface DialogProps {
	title?: React.ReactNode;
	header?: React.ReactNode;
	footer?: React.ReactNode;
	contentBelowBody?: React.ReactNode;
	show: boolean;
	className?: string;
	hideOnEscape?: boolean;
	hideOnBackdropClick?: boolean;
	setBodyPaddingToZero?: boolean;
	size?: "sm" | "lg" | "xl";
	// If we find ourselves needing the rest of the fullScreen classes
	// (sm-down, lg-down, xl-down, xxl-down), add them here.
	fullScreen?: true | "md-down" | "lg-down";
	scrollable?: boolean;
	showCloseButton?: boolean;
	jumpToLink?: {
		href: string;
		label: string;
		target?: string;
		onClick?();
	};

	// This is mainly for modals that navigate through content. We want to set .modal-content min-height to 100% in order to
	// prevent the modal from shrinking-then-expanding during content changes.
	setContentToFullHeight?: boolean;

	// By default, the modal will be removed from the dom shortly after it is hidden. This prop prevents that for cases.
	// It may be useful in cases (eg search) where scroll position should be saved when the dialog is reopened.
	preserveComponentWhenHidden?: boolean;
	// Set focus to top of the modal content. This prevents modal action buttons from receiving initial
	// focus and scrolling the content to the bottom.
	focusModalContentTop?: boolean;
	disableFocusTrap?: boolean;
	onHide(): void;
	onScroll?: UIEventHandler<HTMLDivElement>;
	onResize?(): void;

	/**
	 * Force the modal to display in the flow of the content, rather than as a true modal.
	 * This should really only be used for testing/stories.
	 * */
	forceInline?: boolean;
	children?: React.ReactNode;

	"aria-labelledby"?: string;
	"aria-label"?: string;
}

const DO_ANIMATIONS = false; // Animations have been broken for a long time. Setting this to true should make them work again.
interface DialogBodyProps extends DialogProps {}

function useDialogAriaAttributes({
	ariaLabel,
	ariaLabelledBy,
	title,
	modalId,
}: {
	ariaLabel?: string;
	ariaLabelledBy?: string;
	title?: React.ReactNode;
	modalId: string;
}): {
	ariaLabel?: string;
	ariaLabelledBy?: string;
} {
	if (ariaLabel) {
		return { ariaLabel };
	}

	if (ariaLabelledBy) {
		return { ariaLabelledBy };
	}

	if (typeof title === "string") {
		return { ariaLabel: title };
	}

	if (title) {
		return { ariaLabelledBy: `${modalId}-title` };
	}

	return {};
}

export const DialogBody: React.FC<DialogBodyProps> = ({
	children,
	className,
	show: showProp,
	title,
	header,
	footer,
	contentBelowBody,
	hideOnEscape = true,
	hideOnBackdropClick = true,
	onScroll,
	onHide,
	focusModalContentTop = false,
	jumpToLink,
	forceInline,
	size,
	fullScreen,
	scrollable,
	showCloseButton = true,
	setContentToFullHeight,
	setBodyPaddingToZero,
	disableFocusTrap,
	onResize,
	"aria-labelledby": ariaLabelledByProp,
	"aria-label": ariaLabelProp,
}: DialogBodyProps) => {
	const forceInlineRef = useRef(forceInline);
	const [initialRender, setInitialRender] = useState(true);
	const modalIdRef = useRef(uniqueId("modal"));
	const modalRef = useRef<HTMLDivElement>(null);
	const modalDialogRef = useRef<HTMLDivElement>(null);
	const modalBodyRef = useRef<HTMLDivElement>(null);
	const lastMouseDownWasInDialogRef = useRef(false);

	useEffect(() => {
		if (initialRender) {
			// This is needed to ensure that the "show" class is added after the initial render, so that the fade-in transition works.
			setTimeout(() => setInitialRender(false), 0);
		}
	}, [initialRender]);

	const suppressShow = initialRender && DO_ANIMATIONS;
	const show = showProp && !suppressShow;

	useDialogFocusTrap({
		modalRef,
		disableFocusTrap: !!disableFocusTrap,
		show,
	});

	useEffect(() => {
		if (forceInline !== forceInlineRef.current) {
			console.error(
				"Changing the `forceInline` prop after initial render is not supported and may lead to unexpected behavior.",
			);
		}
		forceInlineRef.current = forceInline;
	}, [forceInline]);

	const handleScrollToElement = useCallback(
		(el: Element | string, opts: ScrollToElementOptions = {}) => {
			if (modalBodyRef.current) {
				scrollToElement(el, {
					container: modalBodyRef.current,
					disableToolbarOffset: true,
					...opts,
				});
			}
		},
		[],
	);

	const handleWindowResize = useCallback(() => {
		onResize?.();
	}, [onResize]);

	// Scroll lock: disable on show, enable on hide
	useEffect(() => {
		// Do not apply scroll lock if this dialog is forced to be inline, since it won't be a true modal in that case
		if (forceInlineRef.current) {
			return;
		}
		if (show) {
			openModalCount++;
			document.querySelector("html")?.classList.add("modal-open");

			return () => {
				openModalCount--;
				if (openModalCount <= 0) {
					openModalCount = 0;
					document.querySelector("html")?.classList.remove("modal-open");
				}
			};
		}
	}, [show]);

	const handleKeyUp = useCallback(
		(event: KeyboardEvent) => {
			if (hideOnEscape && event.key === "Escape") {
				onHide();
			}
		},
		[hideOnEscape, onHide],
	);

	// Keyboard handler: register on show, deregister on hide
	useEffect(() => {
		if (show) {
			document.addEventListener("keyup", handleKeyUp);

			return () => {
				document.removeEventListener("keyup", handleKeyUp);
			};
		}
	}, [show, handleKeyUp]);

	// Resize handler: register on show, deregister on hide
	useEffect(() => {
		if (show) {
			window.addEventListener("resize", handleWindowResize);

			return () => {
				window.removeEventListener("resize", handleWindowResize);
			};
		}
	}, [handleWindowResize, show]);

	const handleModalClick = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			if (!hideOnBackdropClick) {
				return;
			}

			const targetModalEl = closest(event.target as Node, ".modal");
			if (targetModalEl !== modalRef.current) {
				return;
			}

			const closestDialog = closest(event.target as Node, ".modal-dialog");
			if (closestDialog === modalDialogRef.current) {
				return;
			}

			if (!lastMouseDownWasInDialogRef.current) {
				onHide();
			}
		},
		[hideOnBackdropClick, onHide],
	);

	const handleModalMouseDown = useCallback(
		(event: React.SyntheticEvent<HTMLDivElement>) => {
			const closestDialog = closest(event.target as Node, ".modal-dialog");
			lastMouseDownWasInDialogRef.current =
				closestDialog === modalDialogRef.current;
		},
		[],
	);

	const showHeader =
		typeof title !== "undefined" ||
		typeof header !== "undefined" ||
		showCloseButton;

	const headerWithoutTitle = !title && !header && showCloseButton === true;

	// Intentionally read stable refs during render for modal id and forceInline semantics.
	/* eslint-disable react-hooks/refs */
	const { ariaLabel, ariaLabelledBy } = useDialogAriaAttributes({
		ariaLabel: ariaLabelProp,
		ariaLabelledBy: ariaLabelledByProp,
		title,
		modalId: modalIdRef.current,
	});

	return (
		<>
			<ScrollToElementContext.Provider value={handleScrollToElement}>
				<CloseContainingDialogContext.Provider value={onHide}>
					{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
					<div
						className={classNames(
							"modal fade",
							className,
							{ show: show },
							{ "modal-inline": forceInlineRef.current },
						)}
						role="dialog"
						style={{ display: showProp ? "block" : "none" }}
						onClick={handleModalClick}
						onMouseDown={handleModalMouseDown}
						onTouchStart={handleModalMouseDown}
						ref={modalRef}
						aria-labelledby={ariaLabelledBy}
						aria-label={ariaLabel}
						aria-modal="true"
					>
						<div
							className={classNames("modal-dialog", {
								"modal-sm": size === "sm",
								"modal-lg": size === "lg",
								"modal-xl": size === "xl",
								"modal-fullscreen": fullScreen === true,
								"modal-fullscreen-md-down": fullScreen === "md-down",
								"modal-fullscreen-lg-down": fullScreen === "lg-down",
								"modal-dialog-scrollable": scrollable === true,
							})}
							role="document"
							ref={modalDialogRef}
						>
							{/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
							{focusModalContentTop ? <span tabIndex={0} /> : null}
							<div
								className={classNames("modal-content", {
									"modal-content-full-height": setContentToFullHeight === true,
								})}
							>
								{showHeader && (
									<div
										className={classNames("modal-header", {
											"border-bottom-0": headerWithoutTitle === true,
										})}
									>
										{title ? (
											<h2
												className="modal-title"
												id={`${modalIdRef.current}-title`}
											>
												{title}
											</h2>
										) : (
											header
										)}

										{showCloseButton && (
											<CloseButton onClick={onHide} aria-label="Close dialog" />
										)}
									</div>
								)}
								{jumpToLink && (
									<div className="jump-to-content">
										<a
											href={jumpToLink.href}
											onClick={jumpToLink.onClick}
											target={jumpToLink.target}
										>
											<span>{jumpToLink.label}</span>{" "}
											<Icon name="arrow-forward" />
										</a>
									</div>
								)}
								<div
									className={classNames("modal-body", {
										"p-0": setBodyPaddingToZero === true,
									})}
									ref={modalBodyRef}
									onScroll={onScroll}
								>
									{children}
								</div>
								{footer && <div className="modal-footer d-block">{footer}</div>}
								{contentBelowBody}
							</div>
						</div>
					</div>
				</CloseContainingDialogContext.Provider>
			</ScrollToElementContext.Provider>
			{!forceInlineRef.current && (
				<div
					className={classNames("modal-backdrop fade", { show })}
					style={{ display: show ? "block" : "none" }}
				/>
			)}
		</>
	);
	/* eslint-enable react-hooks/refs */
};

function useWaitForDialogAnimation() {
	const stopAnimatingTimeoutRef = useRef<number | null>(null);

	const clearStopAnimatingTimeout = useCallback(() => {
		if (stopAnimatingTimeoutRef.current !== null) {
			window.clearTimeout(stopAnimatingTimeoutRef.current);
			stopAnimatingTimeoutRef.current = null;
		}
	}, []);

	const startAnimating = useCallback((): Promise<void> => {
		clearStopAnimatingTimeout();

		return new Promise((resolve) => {
			stopAnimatingTimeoutRef.current = window.setTimeout(() => {
				clearStopAnimatingTimeout();
				resolve();
			}, 200);
		});
	}, [clearStopAnimatingTimeout]);

	// Final cleanup on unmount
	useEffect(() => {
		return () => {
			clearStopAnimatingTimeout();
		};
	}, [clearStopAnimatingTimeout]);

	return startAnimating;
}

function useDialogFocusTrap({
	modalRef,
	disableFocusTrap,
	show,
}: {
	modalRef: React.RefObject<HTMLDivElement | null>;
	disableFocusTrap: boolean;
	show: boolean;
}) {
	const focusTrapRef = useRef<ReturnType<typeof createFocusTrap> | null>(null);
	const startAnimating = useWaitForDialogAnimation();

	useEffect(() => {
		if (!disableFocusTrap && !focusTrapRef.current && modalRef.current) {
			focusTrapRef.current = createFocusTrap(modalRef.current);
		}
	}, [disableFocusTrap, modalRef]);

	// Focus trap: activate on show (with animation delay), deactivate on hide
	useEffect(() => {
		if (show) {
			void startAnimating().then(() => {
				focusTrapRef.current?.activate();
			});

			return () => {
				focusTrapRef.current?.deactivate();
			};
		}
	}, [show, startAnimating]);
}

/**
 * Avoid rendering the actual dialog component until `props.show` is true, but delay passing down `props.show`
 * briefly to make sure the transition works as expected.
 */
const DialogTransitionWrapper: React.FC<DialogProps> = (props) => {
	const { children, show, preserveComponentWhenHidden, ...dialogProps } = props;
	const [renderDialogBody, setRenderDialogBody] = useState(show);
	const [showDialog, setShowDialog] = useState(show);
	const stopRenderingDialogTimeoutRef = useRef<number | null>(null);
	const showRef = useRef(show);
	const previousShowRef = useRef(show);

	const cancelStopRenderingDialogTimeout = useCallback(() => {
		if (stopRenderingDialogTimeoutRef.current !== null) {
			clearTimeout(stopRenderingDialogTimeoutRef.current);
			stopRenderingDialogTimeoutRef.current = null;
		}
	}, []);

	useEffect(() => {
		showRef.current = show;
	}, [show]);

	useEffect(() => {
		const previousShow = previousShowRef.current;

		if (!previousShow && show) {
			cancelStopRenderingDialogTimeout();
			if (!renderDialogBody) {
				queueMicrotask(() => {
					if (showRef.current) {
						setRenderDialogBody(true);
					}
				});
			}
			requestAnimationFrame(() => {
				if (showRef.current) {
					setShowDialog(true);
				}
			});
		}

		if (previousShow && !show) {
			queueMicrotask(() => {
				if (!showRef.current) {
					setShowDialog(false);
				}
			});
			cancelStopRenderingDialogTimeout();
			stopRenderingDialogTimeoutRef.current = window.setTimeout(() => {
				if (!showRef.current) {
					setRenderDialogBody(false);
				}
			}, 300);
		}

		previousShowRef.current = show;
	}, [cancelStopRenderingDialogTimeout, renderDialogBody, show]);

	useEffect(() => {
		return () => {
			cancelStopRenderingDialogTimeout();
		};
	}, [cancelStopRenderingDialogTimeout]);

	if (!renderDialogBody && !preserveComponentWhenHidden) {
		return null;
	}

	return (
		<DialogBody {...dialogProps} show={show && showDialog}>
			{children}
		</DialogBody>
	);
};

export function getDialogErrorComponent(
	_error,
	props: { show: boolean; onHide(): void },
) {
	return <DialogError {...props} />;
}
export const Dialog = inModalRootPortal<DialogProps>(
	withErrorBoundary(DialogTransitionWrapper, getDialogErrorComponent),
);

export const CloseContainingDialogContext = React.createContext<() => void>(
	() => {
		/* no-op, in case this is called from a component which is sometimes, but not currently, inside a Dialog */
	},
);
